import * as SpeechSDK from 'microsoft-cognitiveservices-speech-sdk'
import { StateCreator } from 'zustand'

interface AzureConfig {
  subscriptionKey: string
  region: string
}

// Default config from environment variables
const DEFAULT_AZURE_CONFIG: AzureConfig = {
  subscriptionKey: import.meta.env.VITE_AZURE_CONFIG_SPEECH_KEY || '',
  region: import.meta.env.VITE_AZURE_CONFIG_SPEECH_REGION || '',
}

const VOICE_NAME = 'en-US-JennyNeural'

// Allow overriding the config for testing
let AZURE_CONFIG: AzureConfig = { ...DEFAULT_AZURE_CONFIG }

export function setAzureConfig(config: Partial<AzureConfig>) {
  AZURE_CONFIG = { ...DEFAULT_AZURE_CONFIG, ...config }
}

export interface SpeechState {
  audioOutEnabled: boolean
  speechQueue: string[]
  isProcessing: boolean
  currentSynthesizer: SpeechSDK.SpeechSynthesizer | null
  currentPlayer: SpeechSDK.SpeakerAudioDestination | null
  isAISpeaking: boolean

  setAudioOutEnabled: (enabled: boolean) => void
  addToSpeechQueue: (text: string) => void
  processQueue: () => Promise<void>
  stopCurrentSpeech: () => void
  clearQueue: () => void
  flushSpeech: () => void
  setAISpeaking: (speaking: boolean) => void
}

export const speechSlice: StateCreator<SpeechState> = (set, get) => ({
  audioOutEnabled: false,
  speechQueue: [],
  isProcessing: false,
  currentSynthesizer: null,
  currentPlayer: null,
  isAISpeaking: false,

  setAudioOutEnabled: (enabled) => set({ audioOutEnabled: enabled }),

  addToSpeechQueue: (text) => {
    if (text.trim()) {
      set((state) => {
        if (state.speechQueue.includes(text)) {
          // 'Speech queue already contains this text, skipping'
          return state
        }

        // 'Adding to speech queue:'
        return { speechQueue: [...state.speechQueue, text] }
      })
    }
  },

  stopCurrentSpeech: () => {
    const { currentSynthesizer } = get()
    if (currentSynthesizer) currentSynthesizer.close()
    set({
      currentSynthesizer: null,
      speechQueue: [],
      isProcessing: false,
    })
  },

  clearQueue: () => set({ speechQueue: [] }),

  processQueue: async () => {
    const state = get()
    if (state.isProcessing || !state.audioOutEnabled) return

    console.log('Processing speech queue, length:', state.speechQueue.length)
    set({ isProcessing: true })

    try {
      while (get().speechQueue.length > 0 && get().audioOutEnabled) {
        const nextText = get().speechQueue[0].trim()
        if (!nextText) {
          set((state) => ({ speechQueue: state.speechQueue.slice(1) }))
          continue
        }

        // 'Speaking text:',
        const isAzureAvailable =
          AZURE_CONFIG.subscriptionKey && AZURE_CONFIG.region

        if (isAzureAvailable) {
          try {
            const speechConfig = SpeechSDK.SpeechConfig.fromSubscription(
              AZURE_CONFIG.subscriptionKey,
              AZURE_CONFIG.region
            )
            speechConfig.speechSynthesisVoiceName = VOICE_NAME

            const player = new SpeechSDK.SpeakerAudioDestination()
            const audioConfig = SpeechSDK.AudioConfig.fromSpeakerOutput(player)
            const synthesizer = new SpeechSDK.SpeechSynthesizer(
              speechConfig,
              audioConfig
            )

            set({ currentSynthesizer: synthesizer, currentPlayer: player })

            // Await actual playback completion via synthesisCompleted event
            await new Promise<void>((resolve, reject) => {
              synthesizer.speakTextAsync(
                nextText,
                (result) => {
                  if (
                    result.reason ===
                    SpeechSDK.ResultReason.SynthesizingAudioCompleted
                  ) {
                    // 'speakTextAsync completed'
                    resolve()
                    // TODO: Test the call of this event
                    synthesizer.close()
                  } else {
                    reject(new Error(result.errorDetails))
                  }
                },
                (error) => {
                  reject(error)
                  synthesizer.close()
                }
              )
            })
            // Set end audio end event
            // TODO: Test the call of this event
            player.onAudioEnd = () => {
              set({ audioOutEnabled: false, currentSynthesizer: null })
              state.flushSpeech()
            }
          } catch (error) {
            // Fallback to Web Speech API, then disable flag when done
            await fallbackToWebSpeech(nextText)
          }
        } else {
          // No Azure config: use Web Speech fallback
          await fallbackToWebSpeech(nextText)
        }

        // Small pause before next item
        await new Promise((resolve) => setTimeout(resolve, 250))
        // Remove text from queue
        set((state) => ({ speechQueue: state.speechQueue.slice(1) }))
      }
    } catch (error) {
      console.error('Queue processing error:', error)
    } finally {
      // Always clear processing flag, leave audioOutEnabled false only when playback ended
      set({ isProcessing: false })
    }
  },

  flushSpeech: () => {
    const { currentSynthesizer, currentPlayer } = get()

    // Check if player is open BEFORE closing it to avoid checking isClosed after close()
    // Handle edge cases: if player is null, we still want to close synthesizer
    const playerWasOpen = !currentPlayer || !currentPlayer.isClosed

    if (currentPlayer) {
      currentPlayer.pause()
      currentPlayer.close()
    }

    // Close synthesizer if player was open (or if player is null but synthesizer exists)
    // This fixes the original bug where isClosed was checked after close(), and also
    // handles the edge case where currentPlayer is null but currentSynthesizer exists
    if (currentSynthesizer && playerWasOpen) {
      currentSynthesizer.close()
    }

    if ('speechSynthesis' in window) window.speechSynthesis.cancel()

    set({
      currentSynthesizer: null,
      currentPlayer: null,
      speechQueue: [],
      isProcessing: false,
    })
  },

  setAISpeaking: (speaking) => {
    set({ isAISpeaking: speaking })
  },
})

// Fallback using Web Speech API
async function fallbackToWebSpeech(text: string): Promise<void> {
  if ('speechSynthesis' in window) {
    const utterance = new SpeechSynthesisUtterance(text)
    window.speechSynthesis.speak(utterance)
    await new Promise((resolve) => {
      utterance.onend = resolve
      setTimeout(resolve, 10000)
    })
  }
}
